import React from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import { removeToast } from '../../store/slices/toastSlice';

const ToastContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((state) => state.toast.toasts);

  const handleClose = (id: string) => {
    dispatch(removeToast(id));
  };

  const getToastIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getToastColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  return (
    <Box>
      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.duration}
          onClose={() => handleClose(toast.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbar-root': {
              top: 24,
              right: 24,
            },
          }}
        >
          <Alert
            severity={getToastColor(toast.type)}
            icon={getToastIcon(toast.type)}
            onClose={() => handleClose(toast.id)}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => handleClose(toast.id)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{
              width: '100%',
              minWidth: 300,
              maxWidth: 460,
              borderRadius: 2,
              background: (t) => `linear-gradient(118deg, ${t.palette.secondary.main}22, ${t.palette.primary.main}33)`,
              color: (t) => t.palette.text.primary,
              border: (t) => `1px solid ${t.palette.secondary.main}55`,
              boxShadow: (t) => `0 14px 40px ${t.palette.mode === 'dark' ? 'rgba(28,233,224,0.18)' : 'rgba(2,6,23,0.08)'}`,
              backdropFilter: 'blur(6px)',
              transform: 'translateY(-6px)',
              '& .MuiAlert-icon': {
                fontSize: 24,
                filter: (t) => `drop-shadow(0 4px 12px ${t.palette.primary.main}55)`,
              },
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            {toast.title && (
              <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                {toast.title}
              </AlertTitle>
            )}
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              {toast.message}
            </Typography>
            {toast.action && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={toast.action.onClick}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  {toast.action.label}
                </Button>
              </Box>
            )}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};

export default ToastContainer;
